import { chatLogger } from '../utils/logger';
import { supabase } from '../integrations/supabase/client';

export interface WebhookLogEntry {
  id?: string;
  chatbot_id: string;
  session_id: string;
  trigger_name?: string;
  webhook_url: string;
  payload: Record<string, any>;
  pii_data?: Record<string, any>;
  response_status?: number;
  response_data?: any;
  error_message?: string;
  timestamp: string;
  execution_time_ms?: number;
}

export interface WebhookResponse {
  success: boolean;
  status?: number;
  data?: any;
  error?: string;
  executionTime: number;
}

class WebhookLogger {
  private logs: WebhookLogEntry[] = []; // Keep for fallback

  async logWebhookAttempt(
    chatbotId: string,
    sessionId: string,
    webhookUrl: string,
    payload: Record<string, any>,
    piiData?: Record<string, any>,
    triggerName?: string,
    triggerRuleId?: string,
    conversationId?: string
  ): Promise<string> {
    const logEntry: WebhookLogEntry = {
      id: crypto.randomUUID(),
      chatbot_id: chatbotId,
      session_id: sessionId,
      trigger_name: triggerName,
      webhook_url: webhookUrl,
      payload,
      pii_data: piiData,
      timestamp: new Date().toISOString()
    };

    try {
      // DEBUG: Log Supabase client info for debugging
      console.log(' WEBHOOK LOGGER DEBUG: Attempting database insert', {
        logId: logEntry.id,
        chatbotId: logEntry.chatbot_id,
        sessionId: logEntry.session_id,
        triggerName: logEntry.trigger_name,
        triggerRuleId: triggerRuleId,
        conversationId: conversationId,
        supabaseUrl: supabase.supabaseUrl,
        hasKey: !!supabase.supabaseKey
      });

      // Store in database
      const { data, error } = await supabase
        .from('trigger_logs')
        .insert({
          id: logEntry.id,
          chatbot_id: logEntry.chatbot_id,
          session_id: logEntry.session_id,
          trigger_name: logEntry.trigger_name,
          webhook_url: logEntry.webhook_url,
          payload: logEntry.payload,
          pii_data: logEntry.pii_data,
          timestamp: logEntry.timestamp,
          // Required fields for database schema compatibility
          // Only include trigger_rule_id if provided (to avoid foreign key constraint)
          ...(triggerRuleId && { trigger_rule_id: triggerRuleId }),
          // Only include conversation_id if provided (to avoid foreign key constraint)
          ...(conversationId && { conversation_id: conversationId }),
          status: 'pending',
          executed_at: logEntry.timestamp
        })
        .select()
        .single();

      if (error) {
        console.error(' WEBHOOK LOGGER ERROR: Failed to store webhook log to database:', error);
        // Fallback to memory storage
        this.logs.push(logEntry);
      } else {
        console.log(' WEBHOOK LOGGER SUCCESS: Webhook attempt logged to database', {
          logId: data?.id,
          chatbotId,
          sessionId
        });
        chatLogger.conversation('Webhook attempt logged to database', {
          logId: logEntry.id,
          chatbotId,
          sessionId,
          webhookUrl: this.sanitizeUrl(webhookUrl),
          triggerName
        });
      }
    } catch (error) {
      console.error(' WEBHOOK LOGGER CATCH: Database connection error while logging webhook:', error);
      // Fallback to memory storage
      this.logs.push(logEntry);
    }

    chatLogger.conversation('Webhook attempt logged', {
      logId: logEntry.id,
      chatbotId,
      sessionId,
      webhookUrl: this.sanitizeUrl(webhookUrl),
      triggerName
    });

    return logEntry.id!;
  }

  async updateWebhookResult(
    logId: string,
    response: WebhookResponse
  ): Promise<void> {
    try {
      // Update in database
      const { error } = await supabase
        .from('trigger_logs')
        .update({
          response_status: response.status,
          response_data: response.data,
          error_message: response.error,
          execution_time_ms: response.executionTime,
          status: response.success ? 'success' : 'error'
        })
        .eq('id', logId);

      if (error) {
        console.error('Failed to update webhook log in database:', error);
        // Fallback to memory update
        const logIndex = this.logs.findIndex(log => log.id === logId);
        if (logIndex !== -1) {
          const logEntry = this.logs[logIndex];
          logEntry.response_status = response.status;
          logEntry.response_data = response.data;
          logEntry.error_message = response.error;
          logEntry.execution_time_ms = response.executionTime;
        }
      } else {
        chatLogger.conversation('Webhook result updated in database', {
          logId,
          success: response.success,
          status: response.status,
          executionTime: response.executionTime
        });
      }
    } catch (error) {
      console.error('Database connection error while updating webhook log:', error);
      // Fallback to memory update
      const logIndex = this.logs.findIndex(log => log.id === logId);
      if (logIndex !== -1) {
        const logEntry = this.logs[logIndex];
        logEntry.response_status = response.status;
        logEntry.response_data = response.data;
        logEntry.error_message = response.error;
        logEntry.execution_time_ms = response.executionTime;
      }
    }

    chatLogger.conversation('Webhook result updated', {
      logId,
      success: response.success,
      status: response.status,
      executionTime: response.executionTime
    });
  }

  async sendWebhookWithLogging(
    chatbotId: string,
    sessionId: string,
    webhookUrl: string,
    payload: Record<string, any>,
    piiData?: Record<string, any>,
    triggerName?: string,
    triggerRuleId?: string,
    conversationId?: string
  ): Promise<WebhookResponse> {
    const startTime = Date.now();

    // Log the attempt
    const logId = await this.logWebhookAttempt(
      chatbotId,
      sessionId,
      webhookUrl,
      payload,
      piiData,
      triggerName,
      triggerRuleId,
      conversationId
    );

    try {
      chatLogger.conversation('Sending webhook', {
        logId,
        url: this.sanitizeUrl(webhookUrl),
        payloadKeys: Object.keys(payload),
        hasPiiData: !!piiData
      });

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'OfferChat-Webhook/1.0'
        },
        body: JSON.stringify({
          ...payload,
          chatbot_id: chatbotId,
          session_id: sessionId,
          trigger_name: triggerName,
          timestamp: new Date().toISOString(),
          ...(piiData && { pii_data: piiData })
        })
      });

      const executionTime = Date.now() - startTime;
      let responseData;

      try {
        const responseText = await response.text();
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        responseData = { error: 'Invalid JSON response' };
      }

      const webhookResponse: WebhookResponse = {
        success: response.ok,
        status: response.status,
        data: responseData,
        executionTime
      };

      if (!response.ok) {
        webhookResponse.error = `HTTP ${response.status}: ${response.statusText}`;
      }

      // Update the log entry
      await this.updateWebhookResult(logId, webhookResponse);

      chatLogger.conversation('Webhook completed', {
        logId,
        success: webhookResponse.success,
        status: webhookResponse.status,
        executionTime
      });

      return webhookResponse;

    } catch (error) {
      const executionTime = Date.now() - startTime;
      const webhookResponse: WebhookResponse = {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        executionTime
      };

      // Update the log entry
      await this.updateWebhookResult(logId, webhookResponse);

      chatLogger.error('Webhook failed', {
        logId,
        error: webhookResponse.error,
        executionTime
      });

      return webhookResponse;
    }
  }

  async getLogsForChatbot(chatbotId: string): Promise<WebhookLogEntry[]> {
    try {
      const { data, error } = await supabase
        .from('trigger_logs')
        .select('*')
        .eq('chatbot_id', chatbotId)
        .order('timestamp', { ascending: false })
        .limit(100); // Limit to last 100 logs for performance

      if (error) {
        console.error('Failed to fetch trigger logs from database:', error);
        // Fallback to memory logs
        return this.logs
          .filter(log => log.chatbot_id === chatbotId)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }

      return data || [];
    } catch (error) {
      console.error('Database connection error while fetching trigger logs:', error);
      // Fallback to memory logs
      return this.logs
        .filter(log => log.chatbot_id === chatbotId)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
  }

  async getLogsForSession(sessionId: string): Promise<WebhookLogEntry[]> {
    try {
      const { data, error } = await supabase
        .from('trigger_logs')
        .select('*')
        .eq('session_id', sessionId)
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Failed to fetch session logs from database:', error);
        // Fallback to memory logs
        return this.logs
          .filter(log => log.session_id === sessionId)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }

      return data || [];
    } catch (error) {
      console.error('Database connection error while fetching session logs:', error);
      // Fallback to memory logs
      return this.logs
        .filter(log => log.session_id === sessionId)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
  }

  async clearLogs(chatbotId?: string): Promise<void> {
    try {
      if (chatbotId) {
        const { error } = await supabase
          .from('trigger_logs')
          .delete()
          .eq('chatbot_id', chatbotId);

        if (error) {
          console.error('Failed to clear chatbot logs from database:', error);
        } else {
          chatLogger.conversation('Cleared trigger logs for chatbot', { chatbotId });
        }

        // Also clear from memory fallback
        this.logs = this.logs.filter(log => log.chatbot_id !== chatbotId);
      } else {
        const { error } = await supabase
          .from('trigger_logs')
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

        if (error) {
          console.error('Failed to clear all logs from database:', error);
        } else {
          chatLogger.conversation('Cleared all trigger logs');
        }

        // Also clear memory fallback
        this.logs = [];
      }
    } catch (error) {
      console.error('Database connection error while clearing logs:', error);
      // Fallback to memory clearing
      if (chatbotId) {
        this.logs = this.logs.filter(log => log.chatbot_id !== chatbotId);
      } else {
        this.logs = [];
      }
    }
  }

  private sanitizeUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}`;
    } catch {
      return '[Invalid URL]';
    }
  }
}

// Export singleton instance
export const webhookLogger = new WebhookLogger();
